import { ARTIFACT_DISTRUST, STATE } from '../generated/protocol';
import { BundlerError } from '../errors/BundlerError';
import { CachedFS } from '../FileSystem/CachedFS';
import { RegistryFS, RegistryFileFetcher } from '../FileSystem/RegistryFS';
import { IFrameParentMessageBus } from '../protocol/iframe';
import { AuthService } from '../auth/AuthService';
import { ThemeService } from '../theme/ThemeService';
import { FormFactorService } from '../formFactor/FormFactorService';
import { EditorContextService } from '../editor/EditorContextService';
import { CatalogService } from '../catalog/CatalogService';
import { VcsService } from '../vcs/VcsService';
import { MountService } from '../mounts/MountService';
import type { SandboxMount } from '../mounts/mountState';
import type { IDisposable } from '../utils/Disposable';
import { APP_ROOT, MANIFEST_SIDECAR_PATH, underAppRoot } from '../fsLayout';
import { isTransformable, rootRuntimeDependencies } from '@immediately-run/transpiler';
import { ArtifactStore, type MdxMetadataAdditive, type SeedResult } from './artifacts/artifactStore';
import { formatStampMismatch } from './artifacts/artifactIndex';
import { getEmbeddedToolchain } from './artifacts/embeddedToolchain';
import { BundlerStatus } from '../protocol/message-types';
import { ResolverCache, resolveAsync } from '../resolver/resolver';
import { DEFAULT_EXTENSIONS, importerAwareExtensions } from '../resolver/utils/extensions';
import {
  resolveSelfHostVersionDetailed,
  fetchVendoredModule,
  SelfHostResolutionError,
  type VendorTiming,
} from './registryResolvedModules';
import { gitDependencyNames } from './gitDependency';

// Module-level monotonic clock for the `[ir-perf:addlocal]` sub-phase breakdown
// (LOAD_PROFILING_SPEC §2 phase 3) — falls back to Date.now when unavailable.
const vendorBootNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
import { verifyVendoredFiles, decideIntegrity, type SdkIntegrity, type FileHashes } from './sdkIntegrity';
import { MountLifecycle, type MountContext } from './mountLifecycle';
import { IPackageJSON, ISandboxFile } from '../types';
import { DelayedEmitter, Emitter } from '../utils/emitter';
import { replaceHTML } from '../utils/html';
import * as logger from '../utils/logger';
import { WorkerMessageBus } from '../utils/WorkerMessageBus';
import { NamedPromiseQueue } from '../utils/NamedPromiseQueue';
import { nullthrows } from '../utils/nullthrows';
import { ModuleRegistry } from './module-registry';
import { esmFallbackSubpathPath, parseBareSubpath } from './module-registry/esm-fallback';
import { resolveFromCdnLayout } from './module-registry/cdnLayoutResolve';
import { LocksetSection, locksetEchoContextFor, validateLockset } from './module-registry/lockset';
import { collectLocalEntrySideEffects } from './sideEffectImports';
import { classifyDeclaredEntry } from './entryPoint';
import { Module } from './module/Module';
import { CRYPTO_MODULE_CODE, NODE_SHIMMED_BUILTIN_NAMES, UNSUPPORTED_BUILTIN_MODULE_CODE } from './shims';
import { Preset } from './presets/Preset';
import { getPreset } from './presets/registry';
import { emitPerfMarker } from './perfMarkers';
import { SELF_HOST_BASES } from './moduleOrigins';
import { retryFetch, registerImmutableUrlPrefix } from '../utils/fetch';
import { basename, dirname } from '../utils/path';
import { FrontmatterParseResult, parseFrontmatter } from '@immediately-run/transpiler';
import { withHeadings } from './headings';
import {
  bindContext,
  globToRegex,
  CopyOnWrite,
  InMemory,
  mount,
  mounts,
  umount,
  resolveMountConfig,
  fs as zenfs,
} from '@zenfs/core';

export type TransformationQueue = NamedPromiseQueue<Module>;
export type MetadataChange = {
  type: 'metadata-update';
  update: Record<string, Record<string, any>>;
};

// Self-hosted, versioned packages the bundler resolves from an origin we control
// instead of the sandpack CDN (SDK_PACKAGING_SPEC §5). Files live under
// `<base>/v/<version>/` — the SDK's release CI publishes its `dist/` + a
// `manifest.json` there. Self-hosting makes the pinned version available the
// instant our own CI finishes (immune to npm→CDN replication lag) over an
// SRI-able origin, and is the SOLE delivery path: there is no host-injected
// singleton anymore (copy-sdk.sh / static vendoring removed). Resolution is
// implicit — see `addLocalModules`.
// The map itself lives in `moduleOrigins.ts` — the M3 per-frame CSP
// (`security/m3Csp.ts`, §G1a / R3-234) must allowlist every base, so it is
// single-sourced there and imported here.

// The version fetched for a self-hosted module when the app declares no SDK
// dependency at all (SDK_PACKAGING_SPEC §5.1(c) — a declared-but-non-concrete
// specifier fails closed instead, see resolveSelfHostVersion). Must be a
// version the SDK release CI has published to `/v/<version>/`. Should always
// track the latest published SDK at deploy time; bump it on every SDK release
// (the host's sdk-integrity manifest auto-covers up to the newest tag).
/**
 * Say, once, what a frontmatter seed left out and why (R3-275c, extended to roots).
 *
 * Each of these is a reason an entry the caller expected is MISSING, and a silent one is
 * indistinguishable from a repo that simply had nothing to seed — which is how a damaged
 * sidecar reads as an empty collection with nothing anywhere saying so. One reporter for
 * both seeds (the boot pass and the later-registered-roots pass) so the two cannot drift.
 */
function reportSeedDiagnostics(seed: MdxMetadataAdditive): void {
  if (seed.droppedEntries.length) {
    const n = seed.droppedEntries.length;
    logger.warn(
      `MDX metadata sidecar: dropped ${n} malformed entr${n === 1 ? 'y' : 'ies'} ` +
        `(${seed.droppedEntries
          .map((r) => `${r.root === APP_ROOT ? '' : `${r.root} `}${r.path}: ${r.reason}`)
          .join(', ')}); ` +
        'those files live-scan lazily.',
    );
  }
  // A non-app root's unusable sidecar costs that root its cached metadata and nothing else,
  // so it is named here rather than folded into the app root's own verdict.
  for (const { root, reason } of seed.unusableRoots) {
    logger.warn(`MDX metadata sidecar unusable (${reason}) for root ${root} — its entries are not seeded.`);
  }
  // Two roots claiming one key means one NESTS inside the other. The OWNING root — the
  // innermost, the same one `consult` serves the bytes from — keeps it whatever order they
  // registered in; name the entries that lost, which are the non-owning root's.
  if (seed.collisions.length) {
    logger.warn(
      `MDX metadata sidecar: ${seed.collisions.length} key(s) also declared by a root that does not own ` +
        `them (${seed.collisions.join(', ')}); the innermost enclosing root's sidecar governs each one.`,
    );
  }
}

const DEFAULT_SDK_VERSION = '0.16.0';

// Oldest version each self-hosted module may pin (SDK_PACKAGING_SPEC §5.1(b)).
// Pins below the floor fail closed at resolve time: 0.2.7 ships a fatal
// sandboxUtils↔runtime import cycle that infinite-loops this bundler at boot
// ("Maximum call stack size exceeded"), so booting it would crash anyway —
// fail with a clear error instead.
const SELF_HOST_FLOORS: Record<string, string> = {
  '@immediately-run/sdk': '0.2.8',
};

// Each self-host `/v/<version>/` path encodes the exact version, so its
// responses are immutable and may be served cache-first from the persistent
// (parent-side) cache. Register the `/v/` prefix so retryFetch routes it through
// that cache — and keep it in sync with the parent's IMMUTABLE_URL_ALLOWLIST
// (immediately-run-sandpack immutable-fetch-protocol.ts), which gates what the
// parent will fetch on the opaque-origin iframe's behalf.
for (const base of Object.values(SELF_HOST_BASES)) {
  registerImmutableUrlPrefix(`${base.replace(/\/$/, '')}/v/`);
}

export const DEFAULT_CODE = `
"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
`.trim();

export { CRYPTO_MODULE_CODE, UNSUPPORTED_BUILTIN_MODULE_CODE, UNSUPPORTED_BUILTIN_MODULE_PATH } from './shims';

/**
 * Source for the sandbox's `fs` module. It re-exports the shared filesystem the
 * bundler exposes on the global. That fs is rooted at `/` (so app code can reach
 * the whole tree, including dynamic mounts like `/firestore`) with the working
 * directory at `APP_ROOT`, so relative paths resolve against the repo. The repo
 * is backed by the parent window over the ZenFS Port, so app-code writes there
 * land in the parent filesystem and are reflected back into the editor. Async
 * APIs (\`fs.promises.*\`, callback style) are supported; synchronous APIs are
 * not, since the Port bridge cannot service synchronous calls.
 */
export const SHARED_FS_MODULE_CODE = `
"use strict";
var __fs = (typeof globalThis !== "undefined" && globalThis.__sandpackSharedFs) || null;
if (!__fs) {
  throw new Error("[sandpack] shared filesystem is unavailable");
}
module.exports = __fs;
`.trim();

interface IBundlerOpts {
  messageBus: IFrameParentMessageBus;
  auth: AuthService;
  theme: ThemeService;
  editorContext: EditorContextService;
  catalog: CatalogService;
  vcs: VcsService;
  formFactor: FormFactorService;
  mounts: MountService;
}

const extractMetadata = (file: ISandboxFile): FrontmatterParseResult | null => {
  if (file.path.endsWith('.mdx')) {
    try {
      const parseResult = parseFrontmatter(file.code);
      if (Object.keys(parseResult.data).length > 0) {
        // The empty-frontmatter drop is part of the envelope contract — an entry
        // with no frontmatter stays unindexed, so it contributes no headings row
        // either (its section list is reachable by body read, the additive degrade).
        return parseResult;
      }
    } catch (e) {
      console.warn(`Error parsing metadata for ${file.path}`, e);
    }
  }
  return null;
};

export class Bundler {
  private lastHTML: string | null = null;
  // Public so transformers reached via `Transformer#init(bundler)` can use the
  // parent handshake — the `BabelTransformer` needs `getBabelPort()`.
  messageBus: IFrameParentMessageBus;

  // Auth/account state mirrored from the parent. App code reaches it via the
  // SDK at `module.evaluation.module.bundler.auth` (same path it already uses
  // for `messageBus` / `onMetadataChange`).
  auth: AuthService;

  // Host UI theme mirrored from the parent. Reached by app code via the SDK at
  // `module.evaluation.module.bundler.theme` (getHostTheme / useHostTheme).
  theme: ThemeService;

  // Editor context (the dirty set, §5.3) mirrored from the parent. Reached via
  // the SDK at `module.evaluation.module.bundler.editorContext` (useEditorContext).
  editorContext: EditorContextService;

  // Method catalog (§5.5) mirrored from the parent. Reached via the SDK at
  // `module.evaluation.module.bundler.catalog` (useCatalog).
  catalog: CatalogService;

  // Source-control state (diff/branch/PR summary, §5.3) mirrored from the parent.
  // Reached via the SDK at `module.evaluation.module.bundler.vcs` (useVcsState).
  vcs: VcsService;

  // Form factor of the rendered surface, mirrored from the parent. Reached via
  // the SDK at `module.evaluation.module.bundler.formFactor` (useFormFactor).
  formFactor: FormFactorService;

  // Mounts available to the sandbox, mirrored from the parent. Reached by app
  // code via the SDK at `module.evaluation.module.bundler.mounts`.
  mounts: MountService;

  fs: CachedFS;
  /** Pre-transpiled artifact seeding/consult/write-through (§5.1, §5.3). */
  artifactStore: ArtifactStore;
  moduleRegistry: ModuleRegistry;

  parsedPackageJSON: IPackageJSON | null = null;
  // The host-resolved root package.json delivered out-of-band via IInitConfig
  // (BOOT_SCAFFOLDING_SPEC §3). When set, it is the authoritative source for the
  // app manifest and the bundler does NOT read `/app/package.json` from the FS —
  // so the host need not write a synthesized `package.json` into the CoW. Null on
  // standalone Sandpack / older hosts, where the FS read remains the source.
  configPackageJSON: IPackageJSON | null = null;
  // Map filepath => Module
  modules: Map<string, Module> = new Map();
  transformationQueue: TransformationQueue;
  resolverCache: ResolverCache = new Map();
  // Resolution-RESULT cache ((specifier, dir, extension-order) → resolved path),
  // distinct from resolverCache (which memoizes package.json/tsconfig *content*, not
  // results). The resolution algorithm is the cold-boot cost — ~3k resolveAsync calls
  // over the precompiled node_modules closure, each re-running candidate generation +
  // probes even with cached reads. Node resolution is dir-relative, so two files in
  // the same dir resolve a given specifier identically: keying by dirname dedups them
  // — and the EXTENSION-ORDER component in the key is what lets R3-577's
  // importer-aware reorder coexist with the cache (a .cjs importer and a .js importer
  // in one dir resolve the same specifier differently and must not share an entry).
  // Reset per compile alongside resolverCache, so edits get fresh resolution.
  resolutionCache: Map<string, Promise<string>> = new Map();
  // R3-49d CDN-layout fast path: per-package alias-eligibility verdict (immutable
  // for the closure) + fast-hit/fall-through counters (diagnostic, logged once at
  // boot). Reset per compile alongside the resolution caches.
  private cdnLayoutEligibility: Map<string, boolean> = new Map();
  private cdnFastHits = 0;
  private cdnFallThroughs = 0;

  /**
   * Read-only view of the fast-path counters (R3-577 review: a test that claims an
   * answer came "through the fast path" must pin it — the registry-backed fs can
   * also satisfy the full resolver, so the resolved path alone proves nothing about
   * WHICH path produced it; the boot log prints these same numbers at ir-perf:cdn-resolve).
   */
  get cdnFastPathStats(): { fastHits: number; fallThroughs: number } {
    return { fastHits: this.cdnFastHits, fallThroughs: this.cdnFallThroughs };
  }

  // Filepaths of modules whose evaluation is in progress (synchronous require
  // chain), used by Module.evaluate to detect import cycles instead of
  // recursing into a stack overflow.
  evaluationStack: string[] = [];
  hasHMR = false;
  isFirstLoad = true;
  preset: Preset | undefined;

  // Map from module id => parent module ids
  initiators = new Map<string, Set<string>>();
  runtimes: string[] = [];

  // Resolved modules harvested from the local entry's (`src/main.tsx`)
  // side-effect imports — CSS, polyfills — applied before the app renders so a
  // default Vite scaffold "just works" (DG-2 / decision #27). Populated on the
  // first compile; evaluated in the first-load branch of the evaluate callback.
  private sideEffectModulePaths: string[] = [];

  // How long the first compile waits for a declared git-library's mount to be
  // pushed by the host before letting resolution fail (LIBRARY_MOUNTS_SPEC L3
  // wait-for-arrival). Tests shorten it.
  libraryMountWaitMs = 30_000;

  private readonly mountLifecycle = new MountLifecycle();
  private onMetadataChangeEmitter = new DelayedEmitter<MetadataChange>();
  onMetadataChange = this.onMetadataChangeEmitter.event;

  private onStatusChangeEmitter = new Emitter<BundlerStatus>();
  onStatusChange = this.onStatusChangeEmitter.event;

  private _previousDepString: string | null = null;
  // Injectable RegistryFS lazy-fetch function (the G0-0 harness injects a network
  // spy so a test can observe lazy unpkg fetches). Undefined → RegistryFS uses its
  // built-in `retryFetch`-through-the-parent-cache default.
  private registryFetcher?: RegistryFileFetcher;
  // Guards `setupModuleMounts()` so the bundler-owned mounts are assembled exactly
  // once (it is called both from `index.ts` after construction and defensively at
  // the top of `compile()`).
  private mountsReady = false;
  private lastMetadata: Map<string, Record<string, any>> = new Map();
  // Host-pinned SDK integrity hashes (SDK_PACKAGING_SPEC §5.2), set from
  // IInitConfig before the initial compile; undefined → verification skipped.
  private sdkIntegrity?: SdkIntegrity;

  // The §5.2 dirty set: repo-relative paths in the COW writable layer (edited in
  // a previous session) + the journal's deleted set, pushed by the host on
  // register-frame. Consulted by artifact seeding so a dirty path is never seeded
  // (its `/app` content no longer matches the zip the artifact was built from).
  private dirtyPaths: Set<string> = new Set();

  // The single `WorkerMessageBus` over the parent-transferred Babel `MessagePort`
  // (§8.x). BOTH the `BabelTransformer` (`transform`) and the `MDXTransformer`
  // (`mdx-compile`, R3-150) talk to the one parent-owned transform worker over this
  // one port, so they MUST share one bus: two buses on one port would each start
  // their own message-id counter at 0 and cross-resolve each other's responses.
  private babelWorkerBus: Promise<WorkerMessageBus> | null = null;

  /**
   * Lazily open (once) the shared bus to the parent-owned transform worker. The
   * parent transfers the `MessagePort` during `register-frame`; an opaque-origin
   * iframe can't spawn a same-origin worker itself, so it drives the port instead.
   */
  getBabelWorkerBus(): Promise<WorkerMessageBus> {
    if (!this.babelWorkerBus) {
      this.babelWorkerBus = (async () => {
        const port = await this.messageBus.getBabelPort();
        port.start();
        return new WorkerMessageBus({
          channel: 'sandpack-babel',
          endpoint: port,
          handleNotification: () => Promise.resolve(),
          handleRequest: () => Promise.reject(new Error('Unknown method')),
          handleError: (err) => {
            logger.error(err);
            return Promise.resolve();
          },
          timeoutMs: 30000,
        });
      })();
    }
    return this.babelWorkerBus;
  }

  /**
   * Records files the parent reported as changed. ZenFS's `Port` backend does
   * not forward watch events across the iframe boundary, so the bundler cannot
   * observe parent-side writes on its own — the parent relays the changed paths
   * (see the `fs-change` handler in `index.ts`). This invalidates the cached
   * contents and queues the paths for the next incremental compile.
   */
  markFilesChanged(paths: string[]): void {
    this.fs.markChanged(paths);
  }

  constructor(options: IBundlerOpts) {
    this.transformationQueue = new NamedPromiseQueue(true, 50);
    this.moduleRegistry = new ModuleRegistry(this);
    // R3-48 G0-4: the bundler reads + writes through a single ZenFS bound context.
    // Bind at the filesystem root (not just the repo) so module resolution can reach
    // the whole tree — `/app` (Port), `/node_modules` (CoW over RegistryFS) and
    // `/transpiled` (tmpfs) are mounts assembled by `setupModuleMounts()` before the
    // first compile; dynamic mounts (`/firestore`) appear as siblings. Repo-relative
    // reads below are anchored to `APP_ROOT` explicitly.
    this.fs = new CachedFS(bindContext({ root: '/', pwd: '/' }));
    this.artifactStore = new ArtifactStore(this.fs, getEmbeddedToolchain());
    this.messageBus = options.messageBus;
    this.auth = options.auth;
    this.theme = options.theme;
    this.editorContext = options.editorContext;
    this.catalog = options.catalog;
    this.vcs = options.vcs;
    this.formFactor = options.formFactor;
    this.mounts = options.mounts;

    // Mount-lifecycle hooks (§11.3). The MDX-frontmatter scan is the first action,
    // scoped to the app root (it skips dynamic mounts — a granted space isn't an
    // app-MDX source by default, and scanning it would be unbounded work). Future
    // post-mount behaviours register here without touching the mount sites.
    this.mountLifecycle.register({
      name: 'mdx-metadata',
      onMount: (ctx) => (ctx.isAppRoot ? this.preloadMDXMetadata() : undefined),
    });
  }

  /** Run post-mount lifecycle actions for a freshly-mounted fs (§11.3). Called by
   *  the runtime at the app-root mount (boot) and each dynamic mount. */
  runPostMount(ctx: MountContext): Promise<void> {
    return this.mountLifecycle.runMount(ctx);
  }

  /** Run pre-unmount lifecycle actions before a mount is torn down (§11.3). */
  runPreUnmount(ctx: MountContext): Promise<void> {
    return this.mountLifecycle.runUnmount(ctx);
  }

  /**
   * Inject the {@link RegistryFS} lazy-fetch function (the G0-0 harness's network
   * spy). MUST be called before {@link setupModuleMounts} mounts `/node_modules`,
   * since the fetcher is captured into the `RegistryFS` instance there.
   */
  setRegistryFetcher(fetcher: RegistryFileFetcher): void {
    this.registryFetcher = fetcher;
  }

  /**
   * Assemble the bundler-owned mount table (R3-48 G0-4, plan §G0-4 step 1):
   *
   *  - `/node_modules` = `CopyOnWrite`: a writable `InMemory` tmpfs over a read-only
   *    {@link RegistryFS} (over THIS bundler's `ModuleRegistry`). Reads are served
   *    from the registry / lazy unpkg fetch; the bundler's own writes
   *    (`registerRuntime` / `addPreloadedModule` / `addLocalModules`) land on the
   *    writable side. RegistryFS owns no journal — registry files are never deleted.
   *  - `/transpiled` = `InMemory` tmpfs (consumed in Phase 03; mounted now so the
   *    spec §3 layout is final).
   *
   * The Bundler owns this assembly (not `index.ts`) because `RegistryFS` needs this
   * bundler's `ModuleRegistry`, which is created in the constructor and circularly
   * references the bundler (plan §G0-4 note). Idempotent: safe to call from
   * `index.ts` after construction AND defensively at the top of `compile()`.
   */
  async setupModuleMounts(): Promise<void> {
    if (this.mountsReady) return;
    this.mountsReady = true;

    const nodeModulesFs = await resolveMountConfig({
      backend: CopyOnWrite,
      readable: new RegistryFS(this.moduleRegistry, this.registryFetcher),
      writable: { backend: InMemory, label: 'node_modules-writable' },
    });
    await this.materializeMountPoint('/node_modules');
    mount('/node_modules', nodeModulesFs);

    const transpiledFs = await resolveMountConfig({ backend: InMemory, label: 'transpiled' });
    await this.materializeMountPoint('/transpiled');
    mount('/transpiled', transpiledFs);

    // The resolver's empty-module shim (EMPTY_SHIM = '/empty.js', resolver/utils)
    // needs a real file to read. It lives on the root tmpfs (not a mount), written
    // once the mount table exists. Replaces the former `//empty.js` MemoryFSLayer write.
    await this.fs.writeFile('/empty.js', 'module.exports = () => {};');
  }

  /**
   * ZenFS routes paths through its mount table alone — a mount point is invisible to
   * `readdir` of its parent unless the dir exists in the underlying fs. Materialize
   * it first (mirrors `index.ts`'s helper); MUST run BEFORE `mount()`, or the mkdir
   * lands inside the freshly-mounted fs instead of the parent.
   */
  private async materializeMountPoint(path: string): Promise<void> {
    try {
      await zenfs.promises.mkdir(path, { recursive: true });
    } catch (err) {
      logger.error(`Failed to materialize mount point ${path}`, err);
    }
  }

  /** Reset all compilation data */
  resetModules(): void {
    this.preset = undefined;
    this.modules = new Map();
    this.resolverCache = new Map();
    this.resolutionCache = new Map();
  }

  async initPreset(preset: string): Promise<void> {
    if (!this.preset) {
      this.preset = getPreset(preset);
      await this.preset.init(this);
    }
  }

  async registerRuntime(id: string, code: string): Promise<void> {
    const filepath = `/node_modules/__csb_runtimes/${id}.js`;
    await this.fs.writeFile(filepath, code);
    const module = new Module(filepath, code, false, this);
    this.modules.set(filepath, module);
    this.runtimes.push(filepath);
  }

  getModule(filepath: string): Module | undefined {
    return this.modules.get(filepath);
  }

  enableHMR(): void {
    this.hasHMR = true;
  }

  getInitiators(id: string): Set<string> {
    return this.initiators.get(id) ?? new Set();
  }

  addInitiator(moduleId: string, initiatorId: string): void {
    const initiators = this.getInitiators(moduleId);
    initiators.add(initiatorId);
    this.initiators.set(moduleId, initiators);
  }

  async processPackageJSON(): Promise<void> {
    // BOOT_SCAFFOLDING_SPEC §3.3 — prefer the host-resolved package.json delivered
    // out-of-band; only fall back to the filesystem read when it is absent
    // (standalone Sandpack / older host).
    if (this.configPackageJSON) {
      this.parsedPackageJSON = this.configPackageJSON;
      return;
    }
    const foundPackageJSON = await this.fs.readFileAsync(underAppRoot('/package.json'));
    try {
      this.parsedPackageJSON = JSON.parse(foundPackageJSON);
    } catch (err) {
      // Makes the bundler a bit more error-prone to invalid pkg.json's
      if (!this.parsedPackageJSON) {
        throw err;
      }
    }
  }

  async resolveEntryPoint(): Promise<string> {
    if (!this.parsedPackageJSON) {
      throw new BundlerError('No parsed package.json found!');
    }

    if (!this.preset) {
      throw new BundlerError('Preset has not been loaded yet');
    }

    const parsed = this.parsedPackageJSON;
    // The manifest's own declarations — the only candidates the BOOT_SCAFFOLDING_SPEC
    // §4.1 classification applies to; the preset defaults below are the boot
    // scaffolding's and always resolve to what they resolve to.
    const declaredEntries = new Set([parsed.main, parsed.source, parsed.module].filter((e) => typeof e === 'string'));
    const potentialEntries = new Set(
      [parsed.main, parsed.source, parsed.module, ...this.preset.defaultEntryPoints].filter(
        (e) => typeof e === 'string',
      ),
    );

    for (let potentialEntry of potentialEntries) {
      if (typeof potentialEntry !== 'string') continue;

      let resolvedEntryPoint: string;
      try {
        // Entry paths from package.json (and preset defaults) are
        // repo-relative. The bundler fs is now rooted at `/`, so anchor them
        // to `APP_ROOT`: an absolute entry like `/index.tsx` means the repo's
        // `/app/index.tsx`, and relative/bare entries resolve against an
        // `APP_ROOT` base.
        const entryPoint =
          potentialEntry[0] === '/'
            ? underAppRoot(potentialEntry)
            : potentialEntry[0] !== '.'
            ? `./${potentialEntry}`
            : potentialEntry;
        resolvedEntryPoint = await this.resolveAsync(entryPoint, underAppRoot('/index.js'));
      } catch (err) {
        logger.debug(`Could not resolve entrypoint ${potentialEntry}`);
        logger.debug(err);
        continue;
      }

      if (declaredEntries.has(potentialEntry)) {
        const verdict = classifyDeclaredEntry(resolvedEntryPoint, await this.readEntrySource(resolvedEntryPoint));
        if (verdict === 'dispatch') {
          // The manifest names a file the boot-defaults entry dispatches anyway —
          // it selects the component, it is not the bundle entry. Skip it so the
          // preset defaults resolve the boot scaffolding `index.tsx`.
          logger.debug(
            `Entrypoint ${potentialEntry} is the boot-dispatched program component; keeping the boot-defaults entry`,
          );
          continue;
        }
        if (verdict === 'refuse') {
          const field = (['main', 'source', 'module'] as const).find((f) => parsed[f] === potentialEntry);
          throw new BundlerError(
            `package.json "${
              field ?? 'main'
            }" names ${potentialEntry}, which default-exports a component but calls no render API, so evaluating it as the entry renders nothing. ` +
              `Name the program component src/App.tsx (the boot entry dispatches it), or mount it from an entry that calls createRoot/boot.`,
          );
        }
      }

      return resolvedEntryPoint;
    }
    throw new BundlerError(
      `Could not resolve entry point, potential entrypoints: ${Array.from(potentialEntries).join(
        ', ',
      )}. You can define one by changing the "main" field in package.json.`,
    );
  }

  /**
   * The declared entry's source for the §4.1 classification, or `undefined` when
   * unreadable — an unreadable entry is used as-is rather than refused on no
   * evidence (a read failure is a resolution concern the candidates after this one
   * may still answer).
   */
  private async readEntrySource(resolvedEntryPoint: string): Promise<string | undefined> {
    try {
      return await this.fs.readFileAsync(resolvedEntryPoint);
    } catch {
      return undefined;
    }
  }

  /**
   * Best-effort read of the cache zip's sidecar lockset
   * (PRETRANSPILED_ARTIFACTS_SPEC §5.4). The sidecar exists inside the mounted
   * repo only when it was loaded from a cache zip — REST loads keep their
   * manifest outside the mount — so this scopes the optimization to exactly
   * the zip path. Any read/parse/validation failure means "no lockset" and the
   * registry resolves dependencies live, as before.
   */
  private async readSidecarLockset(): Promise<LocksetSection | undefined> {
    try {
      const raw = await this.fs.readFileAsync(underAppRoot(MANIFEST_SIDECAR_PATH));
      return validateLockset((JSON.parse(raw) as { lockset?: unknown }).lockset) ?? undefined;
    } catch (err) {
      return undefined;
    }
  }

  async loadNodeModules() {
    if (!this.parsedPackageJSON) {
      throw new BundlerError('No parsed pkg.json found!');
    }

    // R3-289: at the root of a run there is no consumer to satisfy a peer — the
    // platform is the environment — so the root package's non-optional
    // `peerDependencies` are fetched like dependencies (`dependencies` winning
    // conflicts, optional peers skipped). Without this, a dual-role package
    // (library AND app) that declares react as a peer dies with a module-not-found
    // naming react-error-boundary, and a peer-only SDK pin silently self-hosts the
    // (very stale) default. The merge is the transpiler's shared
    // `rootRuntimeDependencies` so the CLI's lockset echo matches (§4.4).
    let dependencies = rootRuntimeDependencies(this.parsedPackageJSON);
    if (Object.keys(dependencies).length > 0) {
      // R3-844: the lockset echo the CLI writes mirrors the PRE-strip input —
      // `computeInputDepMap` over the root runtime deps, git-form entries
      // included — while `dependencies` below has them (and the self-hosted
      // names) stripped. The one shared constructor (lockset.ts) computes it
      // here, BEFORE the strip, so the echo-match is one computation on both
      // sides and the applied manifest can drop the mount-resolved git names.
      // Without this, a git-library consumer's lockset could never match its
      // own echo and the boot always needed the /dep_tree CDN call — a CDN
      // outage blanked exactly the apps the library-mount rail serves.
      const locksetEcho = locksetEchoContextFor(dependencies);
      // Self-hosted (resolveFromRegistry) modules are already registered as
      // local modules by addLocalModules; strip them so the CDN /dep_tree/ query
      // never has to resolve them (immune to npm→CDN replication lag). Their own
      // transitive deps are added by the preset augmentation below regardless.
      const registryResolved = this.registryResolvedNames();
      if (registryResolved.size) {
        dependencies = Object.fromEntries(Object.entries(dependencies).filter(([name]) => !registryResolved.has(name)));
      }
      // R3-293: a mounted git library brings its OWN dependencies. npm would have installed
      // them beside it; the mount path has no such step, so without this the first import
      // that reaches into the library dies on a package the APP never had a reason to
      // declare. Added AFTER the strip (so a library cannot re-introduce a name that
      // resolves from a mount or the self-host) and BEFORE the preset augmentation (so they
      // get the same react/core-js treatment every other dependency does). App-wins on a
      // clash — see `libraryContributedDependencies`.
      const contributed = this.libraryContributedDependencies(dependencies);
      if (Object.keys(contributed).length) {
        dependencies = { ...contributed, ...dependencies };
      }
      dependencies = nullthrows(
        this.preset,
        'Preset needs to be defined when loading node modules',
      ).augmentDependencies(dependencies);

      await this.moduleRegistry.fetchManifest(dependencies, true, await this.readSidecarLockset(), locksetEcho);

      // Load all modules
      await this.moduleRegistry.preloadModules();
      await this.moduleRegistry.loadModuleDependencies();
    }
  }

  async resolveAsync(
    specifier: string,
    filename: string,
    extensions: string[] = DEFAULT_EXTENSIONS,
    retriedEsmFallbackSubpath = false,
  ): Promise<string> {
    // R3-577: a `.cjs` importer tries its CJS sibling FIRST (see
    // resolver/utils/extensions.ts — node-mode interop is only correct when an
    // extensionless internal require meets the `.cjs` build, and the default order
    // hands it the ESM sibling). Reorder BEFORE the cache key so a `.cjs` importer
    // and a `.js` importer in the same dir never share a memoized result.
    extensions = importerAwareExtensions(filename, extensions);
    // Resolution result is a pure function of (specifier, the dir chain from
    // `filename` up, extensions) for the FS snapshot of this compile — node
    // resolution is dir-relative, so two files in the same dir resolve a specifier
    // identically. Memoize on that key to avoid re-running the algorithm for every
    // edge across the closure (the cold-boot cost). Cache the promise so concurrent
    // identical resolutions share one run; drop it on rejection so a miss isn't
    // cached as a permanent failure.
    const key = `${dirname(filename)}\0${specifier}\0${extensions.join(',')}`;
    const cached = this.resolutionCache.get(key);
    if (cached) return cached;
    // R3-49d: resolve relative imports inside precompiled node_modules packages
    // directly from the CDN module layout, skipping the resolution algorithm
    // entirely. Null = the fast path can't/shouldn't handle it → fall through to
    // the full resolver below (correctness degrades gracefully, never breaks).
    // R3-772: the fast path accepts CROSS-package relatives (any package under
    // /node_modules), which the confined resolver refuses — under a confinement
    // root the two must agree, so a confined frame skips the fast path (a
    // snapshot program pays the full resolver; correctness over cold-boot).
    const fast = this.resolutionConfinement
      ? null
      : resolveFromCdnLayout(specifier, filename, extensions, this.moduleRegistry.modules, this.cdnLayoutEligibility);
    if (fast !== null) {
      this.cdnFastHits++;
      const fastPromise = Promise.resolve(fast);
      this.resolutionCache.set(key, fastPromise);
      return fastPromise;
    }
    this.cdnFallThroughs++;
    const promise = resolveAsync(specifier, {
      filename,
      extensions,
      isFile: this.fs.isFile,
      readFile: this.fs.readFile,
      resolverCache: this.resolverCache,
      // R3-772: the snapshot chroot rides every resolution when the host
      // confined this frame (snapshot mounts only); absent otherwise.
      ...(this.resolutionConfinement ? { confineToRoot: this.resolutionConfinement } : {}),
    });
    this.resolutionCache.set(key, promise);
    promise.catch(() => this.resolutionCache.delete(key));
    try {
      return await promise;
    } catch (err) {
      // R3-820 — an esm.sh-fallback package's DEEP SUBPATH import (e.g.
      // `sql.js/dist/sql-asm.js`) fails here because the fallback synthesis
      // registers only the package root: the file map has no subpath file for
      // the resolver to find. Materialize the subpath once (raw tarball file
      // from unpkg, the same bytes the primary CDN would have served), then
      // retry the resolution — the recursion flag makes this fire at most once
      // per call so a subpath that STILL fails keeps its original error.
      if (!retriedEsmFallbackSubpath) {
        const bareSubpath = parseBareSubpath(specifier);
        if (
          bareSubpath !== null &&
          this.moduleRegistry.hasEsmFallback(bareSubpath.name) &&
          (await this.moduleRegistry.ensureEsmFallbackSubpath(bareSubpath.name, bareSubpath.subpath))
        ) {
          // The failed probe memoized the subpath as absent (CachedFS negatives
          // persist per compile; node_modules is exempt from watcher
          // invalidation) — drop that one path so the retry consults the
          // registry-backed mount, which now carries the file. The path is the
          // SAME symbol the registry materialized at, by construction.
          this.fs.dropCachesFor(esmFallbackSubpathPath(bareSubpath.name, bareSubpath.subpath));
          return await this.resolveAsync(specifier, filename, extensions, true);
        }
      }
      // Resolution failure is re-thrown for the caller to handle. Some callers
      // probe for *optional* files (e.g. the `src/main`/`main` local-entry
      // convention in collectLocalEntrySideEffects) and catch this as expected
      // control flow — so logging at `error` here spams the console (and the
      // operator security-events stream) for a non-error. Genuine unresolved
      // imports on the compile path are surfaced + logged at the compile
      // boundary (runCompile's catch → show-error overlay). Keep the resolver
      // trace at `debug` only.
      logger.debug('resolveAsync failed', err);
      throw err;
    }
  }

  private async _transformModule(path: string): Promise<Module> {
    let module = this.modules.get(path);
    if (module) {
      if (module.compiled != null) {
        return Promise.resolve(module);
      } else {
        // compilation got reset, we re-read the source to ensure it's the latest version.
        // reset happens mostly when we receive changes from the editor, so this ensures we actually output the changes...
        try {
          module.source = await this.fs.readFileAsync(path);
        } catch (cause) {
          throw new BundlerError(
            `Failed to re-read source for "${path}" — its source could not be read ` +
              `(usually a failed fetch: a network error or a GitHub API rate limit)` +
              (cause instanceof Error ? `: ${cause.message}` : '') +
              '.',
          );
        }
      }
    } else {
      // §5.3 consult: a covered app source may already be transpiled in
      // `/transpiled` (seeded at boot, or written through earlier this session). A
      // HIT skips the Babel chain entirely — a seeded module is never transpiled
      // live (the §9 "zero babel transforms" criterion). Continue graph traversal
      // from the index's deps exactly as a precompiled CDN module does.
      if (isTransformable(path)) {
        const hit = await this.artifactStore.consult(path);
        if (hit) {
          const seededModule = new Module(path, hit.content, true, this);
          this.modules.set(path, seededModule);
          await Promise.all(hit.deps.map((dep) => seededModule.addDependency(dep)));
          for (const dep of seededModule.dependencies) {
            this.transformModule(dep);
          }
          return seededModule;
        }
      }
      // FAIL FAST + CLEARLY at the original load. A source read fails here when the
      // file never made it into the FS — almost always a FAILED SOURCE FETCH (network
      // error, or a GitHub REST rate limit returning 403 with no CORS header on a
      // cache-cold load), NOT a transpiler bug. Surfacing it now (at load) with an
      // honest message stops it degrading into the misleading downstream
      // "has not been transpiled" thrown much later at require()/evaluate time.
      let content: string;
      try {
        content = await this.fs.readFileAsync(path);
      } catch (cause) {
        throw new BundlerError(
          `Failed to load source for "${path}" — its source could not be read, so it ` +
            `was never transpiled. This is usually a failed source fetch (a network error, ` +
            `or a GitHub API rate limit), not a transpiler bug` +
            (cause instanceof Error ? `: ${cause.message}` : '') +
            '.',
        );
      }
      module = new Module(path, content, false, this);
      this.modules.set(path, module);
    }
    this.refreshMetadata(path, module.source);
    await module.compile();
    // §5.3 write-through: cache a live-transpiled covered source so an in-session
    // re-read that didn't reset THIS module hits the tmpfs instead of the chain.
    if (module.compiled != null && module.compilationError == null && isTransformable(path)) {
      // R3-900: the cache is an optimisation and fails soft. A rejected write is
      // a missed cache entry — the freshly compiled module above is the result;
      // it is never thrown away because persisting it failed. Log once, with the
      // path and the code, and continue.
      try {
        await this.artifactStore.writeThrough(path, module.compiled, [...module.dependencyMap.keys()]);
      } catch (e) {
        const code = (e as { code?: string })?.code;
        logger.warn(
          `transpile-cache write failed for ${path}${code ? ` (${code})` : ''}: ${
            e instanceof Error ? e.message : String(e)
          } — continuing with the compiled module`,
        );
      }
    }
    for (let dep of module.dependencies) {
      const resolvedDependency = await this.resolveAsync(dep, module.filepath);
      this.transformModule(resolvedDependency);
    }
    return module;
  }

  /**
   * Seed every artifact root, then adopt what was seeded. The two belong together: seeding
   * without adopting writes artifacts into `/transpiled` that a pre-registered module would
   * never read, which is the silent-no-op this pairing exists to prevent.
   */
  async seedArtifacts(ctx: { dirtySet: ReadonlySet<string>; writableLayer: ReadonlySet<string> }): Promise<SeedResult> {
    const result = await this.artifactStore.seed(ctx);
    // An artifact entry a root declared for a path a NESTED root owns is not written, so the
    // inner root's bytes are never served under the outer root's name. Say so: a repo whose
    // roots overlap is otherwise told only that fewer files were seeded than its index lists.
    const notOwned = this.artifactStore.takeNotOwnedSeeds();
    if (notOwned.length) {
      logger.warn(
        `Artifact seeding: ${notOwned.length} entr${notOwned.length === 1 ? 'y' : 'ies'} name a path a ` +
          `nested artifact root owns (${notOwned.join(', ')}); the innermost root's own index governs them.`,
      );
    }
    await this.adoptSeededModules();
    // The frontmatter sidecars of the roots that registered since boot, in the one place
    // that already means "every declared root has registered" (MDX_FROM_MOUNT_SPEC §3).
    // `preloadMDXMetadata` runs from the app-root mount hook, which `compile()` fires BEFORE
    // `registerDeclaredGitLibraries` — so without this pass a non-`/app` root's sidecar is
    // never read at all, and the rebase below it is a path nothing reaches.
    await this.seedNewRootMetadata(ctx);
    return result;
  }

  /** Seed (and report on) the frontmatter sidecar of every artifact root not yet seeded.
   *  Additive: it carries no verdict about `/app`, whose live-walk decision was already
   *  taken at boot and must not be re-asked. */
  private async seedNewRootMetadata(ctx: {
    dirtySet: ReadonlySet<string>;
    writableLayer: ReadonlySet<string>;
  }): Promise<void> {
    const seed = await this.artifactStore.seedNewMetadataRoots(ctx);
    for (const [modulePath, frontmatter] of seed.entries) this.seedMetadataEntry(modulePath, frontmatter);
    reportSeedDiagnostics(seed);
  }

  /**
   * Replace already-registered-but-uncompiled modules with their seeded artifacts.
   *
   * The consult in `_transformModule` only runs for a path the module map has never seen.
   * That holds for app sources, which are read lazily — but a git-library's files are
   * WRITTEN AND REGISTERED as `Module`s at mount-registration time
   * (`addGitDependencyModule`), which happens before the seed pass. Without this step every
   * library module would take the "exists, not compiled" branch and be transpiled live, so
   * the library's artifacts would be seeded into `/transpiled` and then never read — the
   * whole point, silently lost.
   *
   * Only ever upgrades: a module that already compiled is left alone, and a consult MISS
   * changes nothing.
   */
  private async adoptSeededModules(): Promise<void> {
    for (const path of this.artifactStore.seededPathList()) {
      const existing = this.modules.get(path);
      if (!existing || existing.compiled != null) continue;
      const hit = await this.artifactStore.consult(path);
      if (!hit) continue;
      const seeded = new Module(path, hit.content, true, this);
      this.modules.set(path, seeded);
      await Promise.all(hit.deps.map((dep) => seeded.addDependency(dep)));
    }
  }

  /** Transform file at a certain absolute path */
  async transformModule(path: string): Promise<Module> {
    let module = this.modules.get(path);
    if (module && module.compiled != null) {
      return Promise.resolve(module);
    }
    return this.transformationQueue.addEntry(path, () => {
      return this._transformModule(path);
    });
  }

  async moduleFinishedPromise(id: string, moduleIds: Set<string> = new Set()): Promise<any> {
    if (moduleIds.has(id)) return;

    const foundPromise = this.transformationQueue.getItem(id);
    if (foundPromise) {
      await foundPromise;
    }

    const asset = this.modules.get(id);
    if (!asset) {
      // Not in the tree ⇒ its transform never registered a module — the source most
      // likely failed to load (a failed fetch: network error or GitHub API rate limit).
      throw new BundlerError(
        `Asset "${id}" is not in the compilation tree — its source likely failed to load ` +
          `(a failed fetch: a network error or a GitHub API rate limit).`,
      );
    } else {
      if (asset.compilationError != null) {
        throw asset.compilationError;
      } else if (asset.compiled == null) {
        throw new BundlerError(
          `Asset "${id}" has not been compiled — its source likely failed to load ` +
            `(a failed fetch: a network error or a GitHub API rate limit).`,
        );
      }
    }

    moduleIds.add(id);

    for (const dep of asset.dependencies) {
      if (!moduleIds.has(dep)) {
        try {
          await this.moduleFinishedPromise(dep, moduleIds);
        } catch (err) {
          logger.debug(`Failed awaiting transpilation ${dep} required by ${id}`);

          throw err;
        }
      }
    }
  }

  private getNodeModuleFiles(moduleName: string, code?: string, packageJSON?: string): [string, string][] {
    return [
      [
        `/node_modules/${moduleName}/package.json`,
        packageJSON ??
          JSON.stringify({
            name: moduleName,
            main: './index.js',
          }),
      ],
      [`/node_modules/${moduleName}/index.js`, code ?? DEFAULT_CODE],
    ];
  }

  async addPreloadedModule(moduleName: string, code?: string, packageJSON?: string): Promise<void> {
    const files = this.getNodeModuleFiles(moduleName, code, packageJSON);
    const manifest = files.filter(([filename, _]) => basename(filename) === 'package.json');
    if (manifest.length !== 1) {
      throw Error('addPreloadedModule did not find manifest for ' + moduleName);
    }
    // The parse is the validation: a malformed manifest throws before any file is written.
    JSON.parse(manifest[0][1]);
    for (let [filepath, contents] of files) {
      await this.fs.writeFile(filepath, contents);
      if (filepath.endsWith('.js')) {
        const module = new Module(filepath, contents, false, this);
        this.modules.set(filepath, module);
      }
    }
  }

  async preloadModules(): Promise<void> {
    // The two shims with their own module sources; every other builtin name in
    // NODE_SHIMMED_BUILTIN_NAMES loads the default polyfill `getNodeModuleFiles`
    // generates. The list is single-sourced in shims.ts so the undeclared-import
    // collector skips exactly these names (R3-1203).
    const shimSources: Record<string, string> = {
      // `fs` is backed by the shared filesystem (parent window over the Port),
      // not a CDN polyfill — so app-code writes reach the parent and reflect in
      // the editor.
      fs: SHARED_FS_MODULE_CODE,
      // WebCrypto-backed subset; unshimmable APIs throw only when called (R3-411).
      crypto: CRYPTO_MODULE_CODE,
    };
    await Promise.all([
      ...NODE_SHIMMED_BUILTIN_NAMES.map((name) => this.addPreloadedModule(name, shimSources[name])),
      // R3-411: the landing spot for a precompiled package's UNRESOLVABLE
      // builtin require (node:child_process, node:worker_threads, …) — a
      // guarded require in an un-imported file must not fail the app.
      this.addPreloadedModule('__node-unsupported', UNSUPPORTED_BUILTIN_MODULE_CODE),
      // this.addPreloadedModule("@internationalized/date"),
    ]);
  }

  async fetchSource(url: string, integrity?: string): Promise<string> {
    // `pinnedSri` makes the immutable cache integrity-aware for this URL: it
    // never serves or persists bytes that don't match (cache-poisoning
    // prevention). Undefined for unpinned URLs (cached by URL as before).
    return await (await retryFetch(url, { pinnedSri: integrity })).text();
  }

  /**
   * Best-effort read of the app's package.json (used by `addLocalModules`, which
   * runs before `processPackageJSON`). Returns `''` on any failure so callers
   * treat it as "no opt-in" and the default injection path is preserved.
   */
  private async readPackageJsonRaw(): Promise<string> {
    // Honour the out-of-band package.json here too (BOOT_SCAFFOLDING_SPEC §3.3):
    // addLocalModules runs before processPackageJSON, so it can't read
    // `this.parsedPackageJSON` yet — consult the config object directly.
    if (this.configPackageJSON) {
      return JSON.stringify(this.configPackageJSON);
    }
    try {
      return await this.fs.readFileAsync(underAppRoot('/package.json'));
    } catch {
      return '';
    }
  }

  /**
   * Fetch a module's `manifest.json` + listed files from `baseUrl` and register
   * them as local modules under `/node_modules/<moduleName>/`. The manifest's
   * file list is generated alongside the files (by the SDK release CI's
   * build-selfhost.mjs), so it cannot drift from the directory contents.
   */
  private async vendorModuleFrom(
    moduleName: string,
    baseUrl: string,
    expectedHashes?: FileHashes,
    timing?: VendorTiming,
  ): Promise<void> {
    const vendored = await fetchVendoredModule(
      moduleName,
      baseUrl,
      (url, integrity) => this.fetchSource(url, integrity),
      expectedHashes,
      timing,
    );
    // SDK_PACKAGING_SPEC §5.2: when the host pinned hashes for this version,
    // verify the fetched bytes BEFORE writing/registering anything — fail the
    // boot closed on any mismatch (a tampered self-host origin must not run).
    const tVerify0 = vendorBootNow();
    if (expectedHashes) {
      const prefix = `/node_modules/${moduleName}/`;
      const result = await verifyVendoredFiles(
        vendored.map((v) => ({
          rel: v.path.startsWith(prefix) ? v.path.slice(prefix.length) : v.path,
          content: v.content,
        })),
        expectedHashes,
      );
      if (!result.ok) {
        console.warn(`[security] sdk-integrity:mismatch ${moduleName}`, {
          mismatches: result.mismatches,
          missing: result.missing,
        });
        throw new SelfHostResolutionError(
          `${moduleName} failed integrity verification (${[...result.mismatches, ...result.missing].join(', ')}). ` +
            `The self-hosted SDK bytes do not match the host-pinned hashes. ` +
            `(SDK_PACKAGING_SPEC §5.2: verification fails closed.)`,
        );
      }
    }
    const tWrite0 = vendorBootNow();
    for (const { path, content, isModule } of vendored) {
      await this.fs.writeFile(path, content);
      if (isModule) {
        this.modules.set(path, new Module(path, content, false, this));
      }
    }
    if (timing) {
      timing.verifyMs += tWrite0 - tVerify0;
      timing.writeMs += vendorBootNow() - tWrite0;
    }
  }

  /**
   * Register a git-dependency library's files as a LOCAL module under
   * `/node_modules/<name>/` (LIBRARY_MOUNTS_SPEC §2.1 L2). This is the
   * registration path that settles Q2: the library is delivered the same proven
   * way `addLocalModules`/`vendorModuleFrom` deliver the SDK — write the files on
   * the CoW writable side and register the module files as bundler `Module`s — so
   * the resolver finds the package by standard node resolution AHEAD of the
   * RegistryFS/CDN (NOT a ZenFS sub-mount under the existing `/node_modules` CoW,
   * whose precedence is uncertain).
   *
   * `files[].path` is relative to the package root (e.g. `package.json`,
   * `dist/index.js`). The name is stripped from the CDN `/dep_tree/` query by
   * `registryResolvedNames()` (git deps). Not wired into the boot sequence — L3's
   * host-mount step calls this; L2 provides it and proves resolution + no-CDN.
   */
  async addGitDependencyModule(
    name: string,
    files: { path: string; content: string; isModule?: boolean }[],
  ): Promise<void> {
    for (const { path, content, isModule } of files) {
      const rel = path.replace(/^\/+/, '');
      const fullPath = `/node_modules/${name}/${rel}`;
      await this.fs.writeFile(fullPath, content);
      if (isModule) {
        this.modules.set(fullPath, new Module(fullPath, content, false, this));
      }
    }
  }

  /** Mount-table entries this bundler created for git libraries (`/node_modules/<name>`).
   *
   *  Tracked because the mount table is GLOBAL and outlives a bundler, while a copied tree
   *  used to be re-copied per app. An alias left behind would shadow the same bare name for
   *  whatever loads next — so an app declaring `"@scope/lib": "^1.0.0"` from npm could
   *  silently get a previous app's git library instead. Dropped on mount-remove and on
   *  teardown. */
  private gitLibraryAliases = new Set<string>();

  /** A git library's OWN `dependencies`, keyed by its module name (R3-293).
   *
   *  npm installs a library's dependency closure alongside it. The library-mount path has
   *  no equivalent step: the CDN `/dep_tree/` query is built from the APP's manifest, and a
   *  git library is stripped from it (it resolves from the mount, not the CDN), so nothing
   *  ever asked for what the library itself needs. The first import that reached one died —
   *  grove's engine on `@immediately-run/mdx-plugins`, which grove declares and a shell
   *  consuming it has no reason to. Captured here at mount time and folded in by
   *  `loadNodeModules`. */
  private gitLibraryDependencies = new Map<string, Record<string, string>>();

  /** Drop one git-library alias (its mount went away), or all of them (teardown). */
  unmountGitLibraryAliases(moduleName?: string): void {
    const targets = moduleName ? [`/node_modules/${moduleName}`] : [...this.gitLibraryAliases];
    for (const target of targets) {
      if (!this.gitLibraryAliases.has(target)) continue;
      try {
        umount(target);
      } catch {
        /* already gone — fine */
      }
      this.gitLibraryAliases.delete(target);
      this.gitLibraryDependencies.delete(target.replace(/^\/node_modules\//, ''));
    }
  }

  /**
   * Receive a host-pushed git-library mount and make it resolvable as
   * `/node_modules/<moduleName>/` (LIBRARY_MOUNTS_SPEC L3 / Q2 registration).
   *
   * This ALIASES the mount — one entry in ZenFS's mount table — rather than copying the
   * repo in. The old shape walked the whole mounted tree, `readFile`d every file, wrote
   * each one under `/node_modules/<name>/`, and registered every module-extension file as
   * a `Module`. Three costs, all avoidable (R3-292):
   *
   *  1. **Reads nobody asked for.** A library mount delivers a REPO, not a published
   *     package: tests, lint and build configs, CI workflows, examples, fixtures. Grove is
   *     ~5k LOC of engine and several hundred KB of things a consumer can never import, and
   *     every byte was read and written on every cold boot before the first import resolved.
   *  2. **A graph nobody asked for.** Eager `Module` registration is what
   *     `adoptSeededModules` keys off (`if (!existing) continue`), so a pre-registered
   *     `eslint.config.js` with a pre-transpiled artifact got ADOPTED and had its
   *     dependencies resolved — dying on `globals`, a devDependency of the library that the
   *     consumer never declared and never imported. That was the R3-292 crash.
   *  3. **A traversal surface.** Building destination paths out of `readdir` names needed
   *     its own name screening (`.`/`..`, `/`, `\`, NUL) to keep a hostile entry from
   *     escaping the package root. Nothing is written now, so that class is gone by
   *     construction rather than defended.
   *
   * What replaces it is the SAME lazy path app sources already take: files are read when
   * something imports them (`_transformModule` reads the path it was asked for), and a
   * covered file takes the §5.3 consult HIT into the library's own pre-transpiled artifacts
   * — which is why `addRoot` still runs. The artifact win of R3-290 is preserved; it simply
   * arrives through the consult rather than through adoption.
   *
   * Read-only comes for free: the aliased fs is the host's `ro` mount, so a write into a
   * library rejects at the same boundary it always did.
   *
   * Idempotent: re-registering re-points the alias at the current mount.
   */
  async registerGitLibraryMount(moduleName: string, mountPath: string): Promise<void> {
    const target = `/node_modules/${moduleName}`;
    const libFs = mounts.get(mountPath);
    if (!libFs) {
      logger.warn(
        `Git-library '${moduleName}': nothing is mounted at ${mountPath}; the bare import ` +
          `will not resolve. (LIBRARY_MOUNTS_SPEC L3.)`,
      );
      return;
    }
    // A scoped name (`@scope/pkg`) needs its scope directory to exist in the underlying
    // registryfs first: `mount()` writes no entry into the parent, so without this the
    // alias is invisible to a `readdir` of `/node_modules/@scope` — and it MUST happen
    // before the mount, or the mkdir lands inside the mounted library instead.
    const parent = target.slice(0, target.lastIndexOf('/'));
    try {
      await zenfs.promises.mkdir(parent, { recursive: true });
    } catch {
      /* already there — fine */
    }
    try {
      umount(target);
    } catch {
      /* not previously aliased — fine */
    }
    mount(target, libFs);
    this.gitLibraryAliases.add(target);
    await this.captureGitLibraryDependencies(moduleName, target);
    // The library's cache zip carries its own pre-transpiled artifacts + manifest sidecar,
    // and they are reachable through the alias. Registering the subtree as an artifact root
    // is what lets them be CONSUMED; seeding runs later in `compile()`, after every declared
    // library has registered.
    this.artifactStore.addRoot(target);
  }

  /**
   * Read a just-aliased library's `package.json` and keep the `dependencies` the consumer
   * will have to resolve on its behalf (R3-293).
   *
   * Two kinds are dropped rather than forwarded to the CDN:
   *
   *  - a **git-form** value (`github:owner/repo#ref`). The CDN cannot resolve it, and
   *    `concreteVersion()` would mis-default it. A library that itself pins a git library is
   *    a transitive mount the host never made, so there is nothing to point at; it fails at
   *    its own import with the normal message rather than corrupting the whole query.
   *  - a **self-hosted** name (the SDK). Those are vendored per-app by `addLocalModules` at
   *    the version the APP pinned; letting a library's pin into the query would either
   *    duplicate the SDK or silently change which one the app runs.
   *
   * `peerDependencies` are deliberately NOT read: a peer is a request that the CONSUMER
   * provide something, which is the app's manifest's job to answer.
   */
  private async captureGitLibraryDependencies(moduleName: string, target: string): Promise<void> {
    let deps: Record<string, string> = {};
    try {
      const raw = await zenfs.promises.readFile(`${target}/package.json`, 'utf8');
      const parsed = JSON.parse(raw as string) as { dependencies?: Record<string, string> };
      const declared = parsed.dependencies ?? {};
      const selfHosted = new Set(Object.keys(SELF_HOST_BASES));
      const gitForm = gitDependencyNames(declared);
      deps = Object.fromEntries(
        Object.entries(declared).filter(([name]) => !selfHosted.has(name) && !gitForm.has(name)),
      );
    } catch {
      // No/unreadable/unparseable package.json: the library still resolves by path, it
      // just contributes nothing to the query. Never fail the mount over it.
      deps = {};
    }
    this.gitLibraryDependencies.set(moduleName, deps);
  }

  /**
   * The union of every mounted git library's own `dependencies`, with the APP's manifest
   * winning any name both declare (R3-293).
   *
   * App-wins because the app's manifest is authoritative for its own tree and the CDN
   * closure is FLAT — there is no nesting to give each side its own copy, so one version
   * has to serve both. Taking the app's keeps the version its author tested. A differing
   * pin is logged once per name rather than resolved silently, because "the library asked
   * for something else and got yours" is exactly the kind of thing that surfaces later as
   * a mystery.
   */
  private libraryContributedDependencies(appDependencies: Record<string, string>): Record<string, string> {
    const contributed: Record<string, string> = {};
    for (const [libName, deps] of this.gitLibraryDependencies) {
      for (const [name, range] of Object.entries(deps)) {
        if (name in appDependencies) {
          if (appDependencies[name] !== range) {
            logger.warn(
              `Git-library '${libName}' asks for ${name}@${range}; the app pins ` +
                `${name}@${appDependencies[name]}, which wins (one flat dependency closure).`,
            );
          }
          continue;
        }
        const existing = contributed[name];
        if (existing !== undefined && existing !== range) {
          logger.warn(
            `Git libraries disagree on ${name} (${existing} vs ${range} from '${libName}'); ` + `keeping ${existing}.`,
          );
          continue;
        }
        contributed[name] = range;
      }
    }
    return contributed;
  }

  /**
   * Boot step (LIBRARY_MOUNTS_SPEC L3): for every declared git-form dependency
   * (`gitDependencyNames`), if it is NOT already registered under
   * `/node_modules/<name>/package.json` (an arrived mount may have registered it
   * already via `handleMountAdd`), look up a mount whose `moduleName === name` in
   * the `MountService` and register it now — WAITING (bounded) for the mount to
   * arrive when it hasn't yet. The host pushes a library `mount-add` only after
   * fetching the repo (commit → tree → blobs over REST), so on a cold boot a
   * real-size library loses the race against the first compile; without the wait
   * the import resolves to `undefined` and the app renders an error the user can
   * only reload away (found live in R3-147). A mount that never arrives within
   * `libraryMountWaitMs` (declined consent, host failure) logs the concise
   * warning and is left to fail resolution normally — the pre-wait behavior,
   * just delayed by the timeout.
   *
   * Wired into `compile()` AFTER `processPackageJSON()` (deps known) and BEFORE
   * `loadNodeModules()` (CDN), guarded by `isFirstLoad`.
   */
  private async registerDeclaredGitLibraries(): Promise<void> {
    const names = gitDependencyNames(rootRuntimeDependencies(this.parsedPackageJSON));
    if (names.size === 0) return;
    for (const name of names) {
      // No "already registered?" probe any more. It used to stat
      // `/node_modules/<name>/package.json` to skip a redundant COPY of the whole tree —
      // expensive work worth avoiding. Registration is now one mount-table write
      // (R3-292), so the probe bought nothing and cost correctness: the alias is global
      // state that outlives a bundler, so a path left over from an earlier registration
      // answered "yes, registered" and pinned the name to a STALE mount. Registering
      // unconditionally is cheaper than the probe was and always points at the mount we
      // actually have. `waitForLibraryMount` still replays synchronously for an
      // already-arrived mount, so the no-wait fast path is unchanged.
      const descriptor = await this.waitForLibraryMount(name, this.libraryMountWaitMs);
      if (!descriptor) {
        logger.warn(
          `Git-library dependency '${name}' declared but its mount did not arrive ` +
            `within ${this.libraryMountWaitMs}ms; the import will resolve normally ` +
            `(likely fail). (LIBRARY_MOUNTS_SPEC L3.)`,
        );
        continue;
      }
      try {
        await this.registerGitLibraryMount(name, descriptor.path);
      } catch (e) {
        logger.warn(`Failed to register git-library '${name}' from ${descriptor.path}`, e);
      }
    }
  }

  /**
   * Resolve when a mount tagged `moduleName === name` is available, or with
   * `null` after `timeoutMs`. Subscribing via `MountService.onChange` replays
   * the current list synchronously, so an already-arrived mount settles the
   * promise immediately. Used only on the first compile (see
   * `registerDeclaredGitLibraries`); a post-boot arrival is registered by
   * `handleMountAdd` but does not yet trigger a recompile (known limitation —
   * a consent granted after the timeout still needs a reload).
   */
  private waitForLibraryMount(name: string, timeoutMs: number): Promise<SandboxMount | null> {
    const mounts = this.mounts;
    if (!mounts || typeof mounts.onChange !== 'function') {
      // Harness/stub without a subscription surface — fall back to a snapshot.
      const hit = mounts?.getMounts?.()?.find((m) => m.moduleName === name) ?? null;
      return Promise.resolve(hit);
    }
    return new Promise((resolve) => {
      let settled = false;
      let disposable: IDisposable | null = null;
      const finish = (m: SandboxMount | null): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        disposable?.dispose();
        resolve(m);
      };
      const timer = setTimeout(() => finish(null), timeoutMs);
      disposable = mounts.onChange((list) => {
        const hit = list.find((m) => m.moduleName === name);
        if (hit) finish(hit);
      });
      // The synchronous replay can settle before `disposable` is assigned.
      if (settled) disposable.dispose();
    });
  }

  /** Host-pinned SDK integrity (SDK_PACKAGING_SPEC §5.2), set from IInitConfig
   *  before the initial compile. Undefined → verification skipped (not wired). */
  setSdkIntegrity(integrity: SdkIntegrity | undefined): void {
    this.sdkIntegrity = integrity;
  }

  /** The §5.2 dirty set, set from IInitConfig before the initial compile.
   *  Undefined/empty → nothing dirty (every artifact eligible). */
  setDirtyPaths(paths: string[] | undefined): void {
    this.dirtyPaths = new Set(paths ?? []);
  }

  /** The out-of-band host-resolved package.json (BOOT_SCAFFOLDING_SPEC §3), set
   *  from IInitConfig before the initial compile. Undefined → the bundler reads
   *  `/app/package.json` from the filesystem as before (standalone fallback). */
  setConfigPackageJSON(pkg: IPackageJSON | undefined): void {
    this.configPackageJSON = pkg ?? null;
  }

  // The snapshot chroot (BUNDLE_EMBEDDING §4c.3, R3-772): when set, module
  // resolution is confined to this root — see IResolveOptionsInput.confineToRoot.
  // Null (the default) is today's unconfined resolution for every other frame.
  private resolutionConfinement: string | null = null;

  /** Confine module resolution to `root` (the frame serves a snapshot-mounted
   *  space program), or lift the confinement with `null`. Drops the resolution
   *  memo so a confined compile never reuses an unconfined hit. The launch path
   *  (R3-773) sets this for snapshot mounts only. */
  setResolutionConfinement(root: string | null): void {
    if (this.resolutionConfinement === root) return;
    this.resolutionConfinement = root;
    this.resolutionCache = new Map();
  }

  /** True if a repo-relative path is dirty (must not be seeded from artifacts). */
  isDirtyPath(repoRelPath: string): boolean {
    return this.dirtyPaths.has(repoRelPath);
  }

  /** Schedule §5.7 spot-verification at idle so it never competes with boot/input. */
  private scheduleSpotVerify(): void {
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(() => void this.runSpotVerify());
    } else {
      setTimeout(() => void this.runSpotVerify(), 0);
    }
  }

  /**
   * Run §5.7 spot-verification: re-transpile a random sample of consumed artifacts
   * and byte-compare. On a mismatch, the store discards the whole section (live
   * transpile from here) and we report the verdict to the parent, which persists
   * the distrust mark for this `(coords, commitSha)` so later boots don't re-consume.
   */
  async runSpotVerify(): Promise<void> {
    // §5.7 byte check PLUS the §3 frontmatter extension: the sampled sources are
    // re-read anyway, so also re-derive their frontmatter and compare to the seeded
    // store — the only runtime path that can catch a forged/divergent sidecar (the
    // consult-HIT path never re-reads a covered `.mdx`; verify-on-read is dead).
    const verdict = await this.artifactStore.spotVerify(undefined, {
      frontmatterOf: (appPath) => this.lastMetadata.get(appPath),
    });
    if (!verdict.tampered) return;
    // §8.14 security event (logged here; the actionable channel is the message below).
    // The specific mismatch kind (§5.7 transpile byte-check vs §3 frontmatter) goes in
    // the log; the wire `reason` stays the stable `spot-verify-mismatch` the parent keys on.
    logger.warn(
      `Artifact spot-verify ${verdict.reason ?? 'mismatch'} at ${
        verdict.path
      } — all artifacts discarded for this session (UI_AS_APPS §8.14).`,
    );
    this.messageBus.sendMessage(ARTIFACT_DISTRUST, {
      commitSha: this.artifactStore.getCommitSha(),
      reason: 'spot-verify-mismatch',
    });
  }

  async addLocalModules(): Promise<void> {
    // Resolve each self-hosted module (the SDK) from its versioned gh-pages
    // location and register its files as local modules. IMPLICIT (no opt-in):
    // the app's pinned dependency version wins, else DEFAULT_SDK_VERSION — so the
    // SDK always resolves as a plain dependency with no `resolveFromRegistry`
    // ceremony. This is the SOLE delivery path; the host-injected singleton
    // (copy-sdk.sh vendoring) is gone. Read package.json directly because this
    // runs before `processPackageJSON`.
    const raw = await this.readPackageJsonRaw();
    // [ir-perf:addlocal] sub-phase breakdown of SDK vendoring (the dominant
    // addLocalModules cost; LOAD_PROFILING_SPEC §2). Accumulated across modules.
    const timing: VendorTiming = { manifestMs: 0, filesMs: 0, fileCount: 0, verifyMs: 0, writeMs: 0 };
    for (const [moduleName, base] of Object.entries(SELF_HOST_BASES)) {
      // Fails closed (SelfHostResolutionError) on a below-floor or
      // non-concrete pin — surfaced as a boot error, never a silent
      // substitute (SDK_PACKAGING_SPEC §5.1). A declaration in either
      // `dependencies` or root `peerDependencies` binds (R3-289).
      const resolvedVersion = resolveSelfHostVersionDetailed(
        raw,
        moduleName,
        DEFAULT_SDK_VERSION,
        SELF_HOST_FLOORS[moduleName] ?? '0.0.0',
      );
      const version = resolvedVersion.version;
      if (resolvedVersion.source === 'default') {
        // R3-289: the silent 0.16.0 landmine, made loud. An app that declares no
        // SDK at all still works ("a plain dependency that just works"), but the
        // author should SEE that the platform chose for them — the default is
        // dozens of releases behind the current SDK and whatever breaks there
        // surfaces far from the cause.
        logger.warn(
          `No ${moduleName} declaration found in this app's package.json (neither ` +
            `"dependencies" nor "peerDependencies") — serving the platform default ` +
            `${DEFAULT_SDK_VERSION}, which is far from the newest release. Pin the SDK ` +
            `(e.g. "${moduleName}": "^0.57.0") to control the version you run on.`,
        );
      }
      const baseUrl = `${base.replace(/\/$/, '')}/v/${version}`;
      logger.debug(`Resolving ${moduleName}@${version} from self-host ${baseUrl}`);
      // SDK_PACKAGING_SPEC §5.2: a MISSING manifest entry for the resolved
      // version must fail closed, exactly like a hash mismatch — otherwise an
      // attacker who can influence the resolved version (or strip a version from
      // a tampered origin) sidesteps verification by landing on an unpinned one.
      // `decideIntegrity` enforces this only once the host has wired integrity;
      // with no host pin, verification is intentionally skipped (the guarantee
      // is inactive — see sdkIntegrity.ts module note), not failed.
      const decision = decideIntegrity(this.sdkIntegrity, moduleName, version);
      if (decision.action === 'fail-closed') {
        console.warn(`[security] sdk-integrity:missing-version ${moduleName}@${version}`);
        throw new SelfHostResolutionError(
          `${moduleName}@${version} has no host-pinned integrity entry. ` +
            `The host delivered an SDK integrity manifest but it does not cover the resolved version. ` +
            `(SDK_PACKAGING_SPEC §5.2: a missing manifest entry fails closed.)`,
        );
      }
      await this.vendorModuleFrom(
        moduleName,
        baseUrl,
        decision.action === 'verify' ? decision.hashes : undefined,
        timing,
      );
    }
    if (this.isFirstLoad) {
      // eslint-disable-next-line no-console
      console.info(
        `[ir-perf:addlocal] manifestFetch ${Math.round(timing.manifestMs)}ms · ` +
          `filesFetch(${timing.fileCount}) ${Math.round(timing.filesMs)}ms · ` +
          `verify ${Math.round(timing.verifyMs)}ms · writeRegister ${Math.round(timing.writeMs)}ms`,
      );
    }
  }

  /**
   * Names resolved from a self-hosted versioned location (those `addLocalModules`
   * registered as local modules). They must be removed from the dependency map
   * before the sandpack-CDN `/dep_tree/` query — otherwise an SDK version not yet
   * replicated npm→CDN would fail resolution for the whole app. Mirrors the CLI
   * lockset's `computeInputDepMap` stripping so the lockset echo-match still
   * holds. Self-hosting is implicit, so every `SELF_HOST_BASES` key is stripped.
   *
   * LIBRARY_MOUNTS_SPEC §2.1/§4 L2: also strip any dependency whose value is a
   * git-form specifier (`github:owner/repo#ref`, …). A git dep resolves from a
   * registered local module under `/node_modules/<name>/`
   * (`addGitDependencyModule`), NOT from the CDN — and `concreteVersion()` would
   * reject/mis-default a `github:` value if it reached the `/dep_tree/` query, so
   * it MUST be stripped here. (Runs in `loadNodeModules`, after
   * `processPackageJSON`, so `parsedPackageJSON` is available.)
   */
  registryResolvedNames(): Set<string> {
    const names = new Set(Object.keys(SELF_HOST_BASES));
    // R3-289: read through the root-peer merge so a git-form PEER mounts (and is
    // stripped from the CDN query) exactly like a git-form dependency.
    for (const name of gitDependencyNames(rootRuntimeDependencies(this.parsedPackageJSON))) {
      names.add(name);
    }
    return names;
  }

  async preloadMDXMetadata(): Promise<void> {
    // Cached path (MDX_CONTENT_COLLECTIONS_SPEC §1.3): seed the store from the
    // frontmatter sidecar — a blob-SHA-confined JSON — instead of the recursive
    // walk + per-file read across the COW port. Clean covered files come from JSON;
    // modified `.mdx` (dirty set, parent-attested — no in-iframe walk) re-scan live;
    // an absent/rejected sidecar falls through to the full live walk below.
    //
    // The seed covers every artifact root, not just `/app` (MDX_FROM_MOUNT_SPEC §3), so a
    // dispatched content mount's own sidecar populates the store too. The live walk below
    // stays `/app`-scoped: it is the fallback for the app repo, and a mount that shipped
    // no sidecar is read lazily by `refreshMetadata` rather than scanned at boot.
    const seed = await this.artifactStore.seedMdxMetadata({
      dirtySet: this.dirtyPaths,
      writableLayer: this.dirtyPaths,
    });
    // Seed every root's entries BEFORE branching on the app root's verdict. Seeding is
    // additive and per-root (MDX_FROM_MOUNT_SPEC §3): a content mount's sidecar is the
    // only source for that mount's frontmatter, because the live walk below is scoped to
    // `APP_ROOT`. Charging a mount's entries to whether `/app` shipped a usable sidecar
    // would drop them with nothing behind them.
    for (const [modulePath, frontmatter] of seed.entries) this.seedMetadataEntry(modulePath, frontmatter);
    reportSeedDiagnostics(seed);
    if (seed.securityReject) {
      logger.warn(
        `MDX metadata sidecar rejected (${seed.securityReject}) — live-scanning frontmatter (UI_AS_APPS §8.14).`,
      );
      this.messageBus.sendMessage(ARTIFACT_DISTRUST, {
        commitSha: this.artifactStore.getCommitSha(),
        reason: seed.securityReject,
      });
      // fall through to the live walk
    } else if (seed.unusable) {
      // R3-275c: the sidecar exists but this reader cannot interpret it (unknown
      // `schemaVersion`, unparseable JSON, a non-object `files`). It is a CACHE over
      // the live walk, so a damaged one must cost the repo its speed, never its
      // frontmatter — the old code seeded an empty store and returned, leaving every
      // clean `.mdx` with no metadata and nothing anywhere saying why.
      //
      // Deliberately NOT `artifact-distrust`, unlike the writable-layer path above:
      // that message tells the parent to persist a distrust mark for this (repo,
      // commitSha) and disables the whole pre-transpiled artifact section until a new
      // commit clears it. A malformed sidecar is a build-output defect, not evidence of
      // tampering, and there is no reason to take the transpiled artifacts down with it.
      logger.warn(`MDX metadata sidecar unusable (${seed.unusable}) — live-scanning frontmatter instead.`);
      // fall through to the live walk
    } else if (seed.present) {
      // Modified `.mdx` re-scan live (cache==live under edits); the rest is covered.
      await Promise.all(
        [...this.dirtyPaths]
          .filter((relPath) => relPath.endsWith('.mdx'))
          .map(async (relPath) => {
            const appPath = underAppRoot(relPath);
            try {
              const source = await this.fs.readFileAsync(appPath);
              this.refreshMetadata(appPath, source);
            } catch {
              /* deleted/unreadable — no entry, matching a live scan */
            }
          }),
      );
      return;
    }

    const re = globToRegex('/**/*.mdx');
    const zenFsLayer = this.fs;
    const fsp = zenFsLayer.boundContext.fs.promises;

    // Walk `APP_ROOT` MANUALLY instead of with readdir's `{recursive:true}`.
    // `/app` is a port-backed COW mount (the parent-hosted working tree); ZenFS's
    // recursive walk descends by calling the MOUNTED fs directly per sub-entry,
    // which throws `ENOENT '/app'` across that mount boundary (single-path reads
    // like `/app/package.json` work — only the recursive enumeration breaks).
    // Resolving each absolute subpath through the vfs (the way file reads already
    // do) avoids it; a subdirectory that still can't be listed is skipped rather
    // than aborting the whole scan. Scope stays the repo (`APP_ROOT`) — dynamic
    // mounts (e.g. `/firestore`) and virtual node_modules aren't app-MDX sources.
    const mdxFiles: string[] = [];
    const walk = async (dir: string): Promise<void> => {
      let names: string[];
      try {
        names = await fsp.readdir(dir);
      } catch {
        return; // unreadable directory — skip, never fail the scan
      }
      await Promise.all(
        names.map(async (name) => {
          const full = `${dir}/${name}`;
          let isDir: boolean;
          try {
            isDir = (await fsp.stat(full)).isDirectory();
          } catch {
            return; // vanished between readdir and stat — skip
          }
          if (isDir) await walk(full);
          else if (full.match(re)) mdxFiles.push(full);
        }),
      );
    };
    await walk(APP_ROOT);

    await Promise.all(
      mdxFiles.map(async (filepath) => {
        try {
          const source = await zenFsLayer.readFileAsync(filepath);
          this.refreshMetadata(filepath, source);
        } catch {
          /* file vanished mid-scan — ignore (lazy refreshMetadata still covers it) */
        }
      }),
    );
  }

  /**
   * Lazily extracts MDX frontmatter metadata when a file is (re-)read during
   * compilation. Fires `onMetadataChange` if the parsed metadata differs from
   * the last value observed for the same path.
   */
  /**
   * A synchronous view of the seeded MDX-frontmatter store for the SDK boot snapshot
   * (MDX_CONTENT_COLLECTIONS_SPEC §1.4). Keyed by the absolute module path — `/app/...`
   * for the app repo (`metadataKey.test.ts`), and `<root>/...` for any other artifact
   * root whose sidecar seeded it (a git library, a dispatched content mount). **Identity contract (load-bearing):** the VALUE objects
   * are the same references the `onMetadataChange` emitter fires (`lastMetadata` values,
   * never a clone), so the SDK's `DelayedEmitter` replay on `enable()` is a no-op via
   * `updateAlreadyApplied`'s reference short-circuit — a defensive clone would re-render
   * the whole collection after first paint (the CLS this feature removes; G-MDX-3
   * carries the zero-re-render-after-`enable()` test).
   */
  getMetadataSnapshot(): Record<string, Record<string, any>> {
    return Object.fromEntries(this.lastMetadata);
  }

  /**
   * Seed one entry from the frontmatter sidecar (§1.3) — sets the store to the
   * pre-parsed `frontmatter` and fires `onMetadataChange` with the SAME ref (the
   * identity contract above), without re-reading or re-parsing source.
   *
   * `modulePath` is absolute and root-joined; it is not necessarily under `/app`, because
   * any artifact root's sidecar can seed (MDX_FROM_MOUNT_SPEC §3).
   */
  private seedMetadataEntry(modulePath: string, frontmatter: Record<string, any>): void {
    const prev = this.lastMetadata.get(modulePath);
    if (prev && JSON.stringify(prev) === JSON.stringify(frontmatter)) return;
    this.lastMetadata.set(modulePath, frontmatter);
    this.onMetadataChangeEmitter.fire({ type: 'metadata-update', update: { [modulePath]: frontmatter } });
  }

  private refreshMetadata(path: string, source: string): void {
    const parsed = extractMetadata({ path, code: source });
    // The additive headings index extension (GROVE_AGENT_SPEC §4): a parsed row
    // carries its entry's heading list, ids from the render canon. The author's
    // own `headings` frontmatter key (if any) wins — `withHeadings` is a no-op then.
    const next = parsed ? withHeadings(parsed.data, parsed.content) : undefined;
    // Metadata is keyed by the file's ABSOLUTE module/fs path (e.g.
    // `/app/content/x.mdx`) — the same identifier `module.dynamicImport`, `fs`,
    // and the SDK `<Include>` use. Keeping the metadata key in the file space
    // (rather than stripping `APP_ROOT` into the URL space) means an app reads a
    // file's metadata and renders it with the SAME path; the URL layer owns the
    // `/files`↔`APP_ROOT` translation (FILES_PREFIX / underAppRoot).
    const prev = this.lastMetadata.get(path);

    const prevKey = prev ? JSON.stringify(prev) : undefined;
    const nextKey = next ? JSON.stringify(next) : undefined;
    if (prevKey === nextKey) {
      return;
    }

    if (next) {
      this.lastMetadata.set(path, next);
    } else {
      this.lastMetadata.delete(path);
    }
    this.onMetadataChangeEmitter.fire({
      type: 'metadata-update',
      update: { [path]: next ?? {} },
    });
  }

  /**
   * Returns the set of paths that changed since the last compile (as reported
   * by the zenfs watcher). Also invalidates the corresponding modules so the
   * transform queue re-reads them.
   */
  private collectChangedFiles(): string[] {
    return this.fs.drainPendingChanges();
  }

  async compile(): Promise<() => any> {
    if (!this.preset) {
      throw new BundlerError('Cannot compile before preset has been initialized');
    }

    // ir.sandbox.boot (R3-46, LOAD_PROFILING_SPEC §3 phase 3): the sandbox is
    // constructed + preset-initialized and the FIRST compile is beginning. This is
    // the earliest in-sandbox instant the bus is guaranteed live, so it brackets
    // "iframe create → ready" (ir.open → here) from the compile-prep + node-module
    // load that follows (here → ir.deps) — the stretch where cold boot spends most
    // of its time. Only on the first load; incremental recompiles are not boot.
    if (this.isFirstLoad) {
      emitPerfMarker(this.messageBus, 'ir.sandbox.boot');
    }

    // First-load boot sub-phase timing (R3-46 diagnostic): the ir.sandbox.boot→ir.deps
    // span is where cold boot spends most of its time, but it bundles several awaits
    // (module-mount setup, SDK vendoring, MDX scan, package.json parse, node-module
    // load). Lap each so the hot one is visible in the console on the next live run;
    // the /app source-read + transpile cost lives in the separate ir.deps→ir.transpile
    // span. Lapping is a no-op off the first load.
    const bootNow = (): number =>
      typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
    const bootPhases: Array<[string, number]> = [];
    let bootLast = bootNow();
    const bootLap = (name: string): void => {
      if (!this.isFirstLoad) return;
      bootPhases.push([name, Math.round(bootNow() - bootLast)]);
      bootLast = bootNow();
    };

    // Ensure the bundler-owned mounts (`/node_modules`, `/transpiled`) exist before
    // the first read/write. Idempotent — `index.ts` already calls this after
    // construction; this guard covers the harness path (and any direct `compile()`).
    await this.setupModuleMounts();
    bootLap('setupModuleMounts');

    this.onStatusChangeEmitter.fire('installing-dependencies');

    // TODO: Have more fine-grained cache invalidation for the resolver
    // Reset resolver cache
    this.resolverCache = new Map();
    this.resolutionCache = new Map();
    this.cdnLayoutEligibility = new Map();
    this.cdnFastHits = 0;
    this.cdnFallThroughs = 0;
    this.fs.resetCache();

    let changedFiles: string[] = [];
    if (!this.isFirstLoad) {
      logger.debug('Started incremental compilation');

      changedFiles = this.collectChangedFiles();

      if (!changedFiles.length) {
        logger.debug('Skipping compilation, no changes detected');
        return () => {};
      }

      // If it's a change and we don't have any hmr modules we simply reload the
      // application. package.json is exempt: it can't be hot-applied anyway and
      // is handled below (`pkgJsonChanged` re-checks the dependencies and
      // reloads only if they actually changed). Without the exemption, a no-op
      // package.json rewrite by the parent (addPackageJSONIfNeeded runs on
      // every register-frame handshake) arrives as an fs-change right after
      // boot — before the app evaluated and registered HMR — and forces a
      // reload, whose handshake rewrites package.json again: an infinite
      // reload loop.
      const changesNeedingHMR = changedFiles.filter((f) => f !== underAppRoot('/package.json'));
      if (!this.hasHMR && changesNeedingHMR.length) {
        logger.debug('HMR is not enabled, doing a full page refresh');
        window.location.reload();
        return () => {};
      }
    } else {
      // First load: files are read lazily from zenfs as the bundler traverses
      // from the entrypoint. Only preloaded/local node_modules are written up
      // front here.
      await this.preloadModules();
      bootLap('preloadModules');
      await this.addLocalModules();
      bootLap('addLocalModules');
      // Drain any spurious watcher events that fired during bootstrap so they
      // aren't interpreted as user-driven changes later.
      this.fs.drainPendingChanges();
      // Fire the app-root mount lifecycle (§11.3) — runs the MDX-metadata scan
      // (and any future post-mount actions). Replaces the former direct
      // preloadMDXMetadata() call. It covers the APP ROOT only, and not because MDX is
      // app-root-scoped — it is not, since R3-168 — but because this is the earliest point
      // in `compile()` and no other root has registered yet. Their sidecars are read in
      // `seedArtifacts`, after `registerDeclaredGitLibraries`.
      await this.runPostMount({ path: APP_ROOT, isAppRoot: true });
      bootLap('runPostMount');
    }

    if (changedFiles.length) {
      const promises = [];
      for (let changedFile of changedFiles) {
        const module = this.getModule(changedFile);
        if (module) {
          module.resetCompilation();
          promises.push(this.transformModule(changedFile));
        }
      }
      await Promise.all(promises);
    }

    const pkgJsonChanged = changedFiles.find((f) => f === underAppRoot('/package.json'));
    if (this.isFirstLoad || pkgJsonChanged) {
      logger.debug('Loading node modules');
      await this.processPackageJSON();
      bootLap('processPackageJSON');

      const depString = Object.entries(this.parsedPackageJSON?.dependencies || {})
        .map((v) => `${v[0]}:${v[1]}`)
        .sort()
        .join(',');

      if (this._previousDepString != null && depString !== this._previousDepString) {
        logger.debug('Dependencies changed, reloading');
        location.reload();
        return () => {};
      }

      this._previousDepString = depString;

      // LIBRARY_MOUNTS_SPEC L3: register any declared git-library whose mount has
      // already arrived under `/node_modules/<name>/` BEFORE the CDN pass, so the
      // bare import resolves from the mounted repo (no CDN fetch). Best-effort.
      if (this.isFirstLoad) {
        await this.registerDeclaredGitLibraries();
        bootLap('registerDeclaredGitLibraries');
      }

      await this.loadNodeModules();
      bootLap('loadNodeModules');

      // R3-46 diagnostic: one console line breaking down the boot→deps span by
      // sub-phase, so the next live run shows which await dominates cold boot.
      if (this.isFirstLoad) {
        const total = bootPhases.reduce((sum, [, ms]) => sum + ms, 0);
        const breakdown = bootPhases.map(([n, ms]) => `${n} ${ms}ms`).join(' · ');
        // eslint-disable-next-line no-console
        console.info(`[ir-perf:boot] ${breakdown} → ir.deps (Σ ${total}ms)`);
        // R3-49d: CDN-layout fast-path coverage (fast hits skip resolveAsync;
        // fall-throughs ran the full resolver).
        // eslint-disable-next-line no-console
        console.info(`[ir-perf:cdn-resolve] fastHits ${this.cdnFastHits} · fallThroughs ${this.cdnFallThroughs}`);
      }

      // ir.deps (R3-46): node-module resolution finished. depCount is the declared
      // dependency count; the host orders marks by timestamp and derives the phase
      // duration from the surrounding marks.
      emitPerfMarker(this.messageBus, 'ir.deps', {
        depCount: Object.keys(this.parsedPackageJSON?.dependencies || {}).length,
      });
    }

    this.onStatusChangeEmitter.fire('transpiling');

    // Transform runtimes
    if (this.isFirstLoad) {
      for (const runtime of this.runtimes) {
        await this.transformModule(runtime);
        await this.moduleFinishedPromise(runtime);
      }
    }

    // §5.1 seed `/transpiled` from the cache zip's pre-transpiled artifacts before
    // entrypoint traversal, so covered sources are consulted (not transpiled)
    // below. Absent/invalid/stamp-mismatched artifacts are a no-op (live boot).
    if (this.isFirstLoad) {
      const seedResult = await this.seedArtifacts({
        dirtySet: this.dirtyPaths,
        writableLayer: this.dirtyPaths,
      });
      if (seedResult.securityReject) {
        // §8.14: a seeding input was present in the writable layer — the whole
        // section is rejected (live transpile for everything this session).
        logger.warn(`Artifact seeding rejected (${seedResult.securityReject}); live transpiling.`);
        // A security reject on one root must not mute another root's stamp-mismatch
        // reason (the two aggregate independently) — the loop below runs for both
        // branches; only the count line is else-scoped.
      } else {
        // `console.info`, not `logger.debug`, and deliberately unconditional (R3-294).
        //
        // This was `logger.debug`, which the host's log level suppresses in practice — so
        // the line never appeared, and its absence was read as "seeding did not happen"
        // for an entire investigation before instrumentation showed 124 of 124 artifacts
        // seeded. A boot fact that a drill's pass criterion is written against has to be
        // OBSERVABLE, which is why the neighbouring `[ir-perf:*]` telemetry uses
        // `console.info` too. Emitting the ZERO case matters just as much: "seeded 0" is
        // the fail-safe working (a REST-fetched library ships no sidecar), and it is
        // indistinguishable from "the code never ran" if it says nothing.
        // eslint-disable-next-line no-console
        console.info(
          `[ir-artifacts] seeded ${seedResult.seeded} pre-transpiled artifact(s) into /transpiled ` +
            `across ${this.artifactStore.rootCount()} root(s)`,
        );
      }
      // R3-843: a stamp mismatch seeds ZERO of a FULL payload — the fail-safe working
      // as designed, and indistinguishable from "no artifacts shipped" in the count
      // line alone. The reason is loud for the same R3-294 discipline as the count:
      // once per root per boot (seed() runs once per root), console.info
      // (default-visible), and in BOTH the rejected and the clean branches above.
      for (const { root, mismatch } of seedResult.stampMismatches ?? []) {
        // eslint-disable-next-line no-console
        console.info(formatStampMismatch(root, mismatch));
      }
    }

    // Resolve entrypoints
    const resolvedEntryPoint = await this.resolveEntryPoint();
    logger.debug('Resolved entrypoint:', resolvedEntryPoint);

    // Transform entrypoint and deps
    const entryModule = await this.transformModule(resolvedEntryPoint);
    await this.moduleFinishedPromise(resolvedEntryPoint);
    logger.debug('Bundling finished, manifest:');
    logger.debug(this.modules);

    entryModule.isEntry = true;

    // DG-2 / decision #27: a default Vite scaffold parks global CSS (and
    // polyfills) in `src/main.tsx` — the local-dev render shim immediately.run
    // never executes (the render entry is the URL-selected `App.tsx`). Harvest
    // that file's *side-effect* imports and fold the resolved modules into the
    // graph so they're applied, WITHOUT running its `render()` call. First load
    // only; the modules then live in the graph and HMR like any other.
    if (this.isFirstLoad) {
      const { sideEffectModules } = await collectLocalEntrySideEffects({
        entryPoint: resolvedEntryPoint,
        resolve: (specifier, from) => this.resolveAsync(specifier, from),
        readFile: (path) => this.fs.readFileAsync(path),
        onWarn: (message, err) => {
          logger.debug(message);
          if (err) logger.debug(err);
        },
      });
      const applied: string[] = [];
      for (const path of sideEffectModules) {
        try {
          await this.transformModule(path);
          await this.moduleFinishedPromise(path);
          applied.push(path);
        } catch (err) {
          // A side-effect import that fails to compile degrades to the pre-#27
          // behaviour (not applied) rather than failing the whole compile.
          logger.error(`Failed compiling local-entry side-effect module ${path}`);
          logger.error(err);
        }
      }
      this.sideEffectModulePaths = applied;
    }

    const transpiledModules = Array.from(this.modules, ([name, value]) => {
      return {
        /**
         * TODO: adds trailing for backwards compatibility
         */
        [name + ':']: {
          source: {
            isEntry: entryModule.filepath === value.filepath,
            fileName: value.filepath,
            compiledCode: value.compiled,
          },
        },
      };
    }).reduce((prev, curr) => {
      return { ...prev, ...curr };
    }, {});

    this.messageBus.sendMessage(STATE, { state: { transpiledModules } });

    // ir.transpile (R3-46): the babel transform pass is complete and the compiled
    // module graph has been forwarded. moduleCount is the size of that graph.
    emitPerfMarker(this.messageBus, 'ir.transpile', { moduleCount: this.modules.size });

    return () => {
      // Evaluate
      logger.debug('Evaluating...');

      if (this.isFirstLoad) {
        for (const runtime of this.runtimes) {
          const module = this.modules.get(runtime);
          if (!module) {
            throw new BundlerError(`Runtime ${runtime} is not defined`);
          } else {
            logger.debug(`Loading runtime ${runtime}...`);
            module.evaluate();
          }
        }

        // Apply the local entry's side effects (CSS/polyfills — DG-2) before
        // the app renders. A failure here degrades the app (e.g. unstyled)
        // rather than crashing it, matching the pre-#27 behaviour.
        for (const path of this.sideEffectModulePaths) {
          const module = this.modules.get(path);
          if (!module) continue;
          try {
            module.evaluate();
          } catch (err) {
            logger.error(`Failed evaluating local-entry side-effect module ${path}`);
            logger.error(err);
          }
        }

        entryModule.evaluate();

        // ir.eval (R3-46): first-load module evaluation finished — the last
        // bundler phase before the app's own root render (the SDK then marks
        // ir.fmp/ir.interactive). moduleCount is the evaluated graph size. Marked
        // only on first load: the canonical request→interactive boot stream.
        emitPerfMarker(this.messageBus, 'ir.eval', { moduleCount: this.modules.size });

        this.isFirstLoad = false;
        // §5.7 mandatory spot-verification: on a boot that consumed artifacts,
        // verify a random sample at idle — never blocking boot or input handling.
        if (this.artifactStore.consumedCount() > 0) {
          this.scheduleSpotVerify();
        }
      } else {
        this.modules.forEach((module) => {
          if (module.hot.hmrConfig?.isDirty()) {
            module.evaluate();
          }
        });

        // TODO: Validate that this logic actually works...
        // Check if any module has been invalidated, because in that case we need to
        // restart evaluation.
        const invalidatedModules = Object.values(this.modules).filter((m: Module) => {
          if (m.hot.hmrConfig?.invalidated) {
            m.resetCompilation();
            this.transformModule(m.filepath);
            return true;
          }

          return false;
        });

        if (invalidatedModules.length > 0) {
          return this.compile();
        }
      }
    };
  }

  // TODO: Support template languages...
  async getHTMLEntry(): Promise<string> {
    let content = undefined;
    for (const filepath of [underAppRoot('/index.html'), underAppRoot('/public/index.html')]) {
      try {
        content = await this.fs.readFileAsync(filepath);
      } catch (err) {}
      if (content) {
        return content;
      }
    }
    // fall back to preset default
    if (!this.preset) {
      throw new BundlerError('Bundler has not been initialized with a preset');
    }
    return this.preset.defaultHtmlBody;
  }

  async replaceHTML() {
    const html = (await this.getHTMLEntry()) ?? '<div id="root"></div>';
    if (this.lastHTML) {
      if (this.lastHTML !== html) {
        window.location.reload();
      }
      return;
    } else {
      this.lastHTML = html;
      replaceHTML(html);
    }
  }
}
